import { useState, useEffect, useMemo } from 'react'
import { match } from 'ts-pattern'
import { useParams } from 'react-router-dom'
import { thermostatsCollection } from '@/reducers/thermostats'
import {
  transformFromApiFormat,
  useThermostatInactivityPeriodsCollection,
  useThermostatInactivityPeriodMutations,
  type InactiveTimeUI,
} from '@/reducers/thermostatInactivityPeriods'
import { getInactivePeriodSummaryLines } from '@/app/pages/thermostat/cost-categories/inactive-times/utils'
import { logException, logMessage } from '@/logging/sentry'
import { useNavigation } from '@/app/hooks'

export type InactiveTime = InactiveTimeUI

export default function useInactiveTimesViewModel() {
  const navigation = useNavigation()
  const { thermostatId: thermostatIdParam } = useParams<{
    thermostatId: string
  }>()
  const thermostatId = Number.parseInt(thermostatIdParam)

  const {
    data: thermostats,
    isLoading: thermostatsLoading,
    refetch: refetchThermostats,
  } = thermostatsCollection.useFetch()

  const thermostat = thermostats.find((t) => t.id === thermostatId)

  // Use the collection to fetch inactivity periods
  const {
    data: inactivityPeriodsData,
    isLoading: inactivityPeriodsLoading,
    refetch: refetchInactivityPeriods,
  } = useThermostatInactivityPeriodsCollection(thermostatId)
  const { create, update, remove } =
    useThermostatInactivityPeriodMutations(thermostatId)

  // Draft model overlays remote data with local-only changes until save
  type DraftExisting = InactiveTime & { kind: 'existing'; isDeleted: boolean }
  type DraftNew = InactiveTime & { kind: 'new' }
  type DraftInactiveTime = DraftExisting | DraftNew

  // Memoized view of server data in UI format
  const originalData = useMemo<InactiveTime[]>(
    () => inactivityPeriodsData.map(transformFromApiFormat),
    [inactivityPeriodsData],
  )

  const [draftInactiveTimes, setDraftInactiveTimes] = useState<
    DraftInactiveTime[]
  >(() => {
    if (inactivityPeriodsLoading) {
      return []
    }
    return originalData.map((it) => ({
      ...it,
      kind: 'existing' as const,
      isDeleted: false as const,
    }))
  })

  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null)
  // Track ids created during this draft session so we can delete them on discard
  const [createdIds, setCreatedIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  // Compute any new remote items not in draft
  const additions = useMemo<DraftExisting[]>(() => {
    if (inactivityPeriodsLoading) {
      return []
    }

    const existingIds = new Set(draftInactiveTimes.map((d) => d.id))
    return originalData
      .filter((it) => !existingIds.has(it.id))
      .map((it) => ({
        ...it,
        kind: 'existing' as const,
        isDeleted: false as const,
      }))
  }, [inactivityPeriodsLoading, originalData, draftInactiveTimes])

  // Minimal effect: only append additions, no other logic
  useEffect(() => {
    if (additions.length > 0) {
      setDraftInactiveTimes((prev) => [...prev, ...additions])
    }
  }, [additions])

  // Visible list strips draft metadata and hides deleted items
  const visibleInactiveTimes = useMemo<InactiveTime[]>(() => {
    return draftInactiveTimes
      .filter((d) => (d.kind === 'existing' ? !d.isDeleted : true))
      .map((d) => {
        const { kind, ...rest } = d as DraftInactiveTime
        // Ensure we do not leak isDeleted to consumers
        if ('isDeleted' in rest) {
          const { isDeleted, ...clean } = rest as DraftExisting
          return clean
        }
        return rest as InactiveTime
      })
  }, [draftInactiveTimes])

  const isLoading = thermostatsLoading || inactivityPeriodsLoading

  // Helper function to check if two InactiveTime objects are equal
  const arePeriodsEqual = (a: InactiveTime, b: InactiveTime): boolean => {
    return (
      a.startTime === b.startTime &&
      a.endTime === b.endTime &&
      a.days.length === b.days.length &&
      a.days.every((day) => b.days.includes(day))
    )
  }

  // Compute if there are unsaved changes
  const hasChanges = useMemo(() => {
    const originalById = new Map(originalData.map((it) => [it.id, it]))

    // New or deleted drafts immediately imply changes
    for (const d of draftInactiveTimes) {
      if (d.kind === 'new' || (d.kind === 'existing' && d.isDeleted)) {
        return true
      }
    }

    // Modified existing items
    for (const d of draftInactiveTimes) {
      if (d.kind === 'existing' && !d.isDeleted) {
        const original = originalById.get(d.id)
        if (!original) {
          return true
        }
        const current: InactiveTime = {
          id: d.id,
          startTime: d.startTime,
          endTime: d.endTime,
          days: d.days,
        }
        if (!arePeriodsEqual(current, original)) {
          return true
        }
      }
    }

    // If any original item is missing from draft, consider it a change
    const draftExistingIds = new Set(
      draftInactiveTimes
        .filter((d) => d.kind === 'existing')
        .map((d) => (d as DraftExisting).id),
    )
    for (const orig of originalData) {
      if (!draftExistingIds.has(orig.id)) {
        return true
      }
    }

    return false
  }, [draftInactiveTimes, originalData])

  // Check if there are any visible periods with no days selected
  const hasInvalidPeriods = useMemo(() => {
    return visibleInactiveTimes.some((period) => period.days.length === 0)
  }, [visibleInactiveTimes])

  // Compute if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    return !hasChanges || hasInvalidPeriods || isSaving
  }, [hasChanges, hasInvalidPeriods, isSaving])

  // Compute if add button should be disabled
  const isAddDisabled = useMemo(() => {
    return hasInvalidPeriods || isSaving
  }, [hasInvalidPeriods, isSaving])

  const onAddInactiveTime = async () => {
    const defaults: InactiveTime = {
      id: '',
      startTime: '22:00',
      endTime: '07:00',
      days: [],
    }
    // Create immediately to get durable id
    const created = await create(defaults)
    setDraftInactiveTimes((prev) => [
      ...prev,
      { ...created, kind: 'existing', isDeleted: false } as DraftExisting,
    ])
    setCreatedIds((prev) => [...prev, created.id])
  }

  const onRemoveInactiveTime = (id: string) => {
    setDraftInactiveTimes((prev) =>
      prev.flatMap((item) =>
        match(item)
          .with({ kind: 'new', id }, () => [])
          .with({ kind: 'existing', id }, (existing) => [
            { ...existing, isDeleted: true } as DraftExisting,
          ])
          .otherwise((other) => [other]),
      ),
    )
  }

  const onUpdateInactiveTime = (
    id: string,
    patch: Partial<Pick<InactiveTime, 'startTime' | 'endTime' | 'days'>>,
  ) => {
    setDraftInactiveTimes((prev) =>
      prev.map((item) =>
        match(item)
          .with({ id, kind: 'existing' }, (existing) =>
            existing.isDeleted ? existing : { ...existing, ...patch },
          )
          .with({ id, kind: 'new' }, (created) => ({ ...created, ...patch }))
          .otherwise((other) => other),
      ),
    )
  }

  const onToggleDay = (id: string, dayIndex: number) => {
    setDraftInactiveTimes((prev) =>
      prev.map((item) =>
        match(item)
          .with({ id, kind: 'existing' }, (existing) => {
            if (existing.isDeleted) {
              return existing
            }
            const has = existing.days.includes(dayIndex)
            const newDays = has
              ? existing.days.filter((d) => d !== dayIndex)
              : [...existing.days, dayIndex]
            return { ...existing, days: newDays }
          })
          .with({ id, kind: 'new' }, (created) => {
            const has = created.days.includes(dayIndex)
            const newDays = has
              ? created.days.filter((d) => d !== dayIndex)
              : [...created.days, dayIndex]
            return { ...created, days: newDays }
          })
          .otherwise((other) => other),
      ),
    )
  }

  const onSave = async () => {
    setIsSaving(true)
    const originalById = new Map(originalData.map((it) => [it.id, it]))

    const toUpdate = draftInactiveTimes.filter(
      (d): d is DraftExisting => d.kind === 'existing' && !d.isDeleted,
    )
    const toDelete = draftInactiveTimes.filter(
      (d): d is DraftExisting => d.kind === 'existing' && d.isDeleted,
    )

    const operations: Array<Promise<void>> = []

    // Updates: only when changed vs original; for items just created this session,
    // ensure we persist any edits by issuing an update regardless of missing original.
    for (const d of toUpdate) {
      const original = originalById.get(d.id)
      if (!original) {
        if (createdIds.includes(d.id)) {
          operations.push(
            update({
              id: d.id,
              startTime: d.startTime,
              endTime: d.endTime,
              days: d.days,
            }),
          )
        }
        continue
      }
      const current: InactiveTime = {
        id: d.id,
        startTime: d.startTime,
        endTime: d.endTime,
        days: d.days,
      }
      if (!arePeriodsEqual(current, original)) {
        operations.push(update(current))
      }
    }

    // Deletes
    for (const d of toDelete) {
      operations.push(remove(d.id))
    }

    try {
      await Promise.all(operations)
      // Clear drafts to avoid duplicate display; effect will repopulate after refetch
      setDraftInactiveTimes([])
      setCreatedIds([])
      await refetchInactivityPeriods()
      await refetchThermostats()
      // Navigate back to the L2
      navigation.goBack()
    } catch (error) {
      logException(error)
    } finally {
      setIsSaving(false)
    }
  }

  const scheduleSummary = getInactivePeriodSummaryLines(visibleInactiveTimes)

  // Confirmation modal handlers
  const onConfirmNavigation = () => {
    setShowConfirmationModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const onCancelNavigation = () => {
    setShowConfirmationModal(false)
    setPendingNavigation(null)
  }

  const onDiscardAndNavigate = async () => {
    // Delete any periods created during this draft session
    if (createdIds.length > 0) {
      try {
        await Promise.all(createdIds.map((id) => remove(id)))
      } catch (e) {
        console.error('Error discarding created inactive times:', e)
      }
    }

    // Revert draft to original without mutating remaining remote items
    setDraftInactiveTimes(
      originalData.map((it) => ({ ...it, kind: 'existing', isDeleted: false })),
    )
    setCreatedIds([])

    setShowConfirmationModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const onSaveAndNavigate = async () => {
    await onSave()
    setShowConfirmationModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const onNavigationAttempt = (navigationCallback: () => void) => {
    if (hasChanges) {
      setPendingNavigation(() => navigationCallback)
      setShowConfirmationModal(true)
    } else {
      navigationCallback()
    }
  }

  return {
    thermostat,
    inactiveTimes: visibleInactiveTimes,
    isLoading,
    hasChanges,
    hasInvalidPeriods,
    isSaveDisabled,
    isAddDisabled,
    scheduleSummary,
    showConfirmationModal,
    onAddInactiveTime,
    onRemoveInactiveTime,
    onUpdateInactiveTime,
    onToggleDay,
    onSave,
    onNavigationAttempt,
    onConfirmNavigation,
    onCancelNavigation,
    onDiscardAndNavigate,
    onSaveAndNavigate,
  }
}
